import React, { useCallback } from 'react'
import StackedCards from '../ui/StackedCards'

interface LandingProductFeature {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  iconColor: string
  iconBg: string
}

const PRODUCT_FEATURES: LandingProductFeature[] = [
  {
    id: 'structured-reports',
    title: 'Structured intelligence report',
    description: 'Start with the full brief. Read a sourced narrative with synthesized context and clear conclusions.',
    iconColor: '#0ea5e9',
    iconBg: '#f0f9ff',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 3h7l5 5v13H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
        <path d="M14 3v6h6" />
        <path d="M10 13h6" />
        <path d="M10 17h6" />
      </svg>
    ),
  },
  {
    id: 'interactive-data',
    title: 'Interactive data',
    description: 'Explore the evidence directly. Move through charts, tables, and tracked metrics inside the same report.',
    iconColor: '#8b5cf6',
    iconBg: '#f5f3ff',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 10h18" />
        <path d="M9 20V10" />
      </svg>
    ),
  },
  {
    id: 'geographic-analysis',
    title: 'Geographic analysis',
    description: 'Compare regions, submarkets, and place-specific dynamics without leaving the report.',
    iconColor: '#10b981',
    iconBg: '#ecfdf5',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    ),
  },
  {
    id: 'financial-scenarios',
    title: 'Financial & scenario analysis',
    description: 'Pressure-test the decision. Model scenarios, challenge assumptions, and see how outcomes change before you commit.',
    iconColor: '#f59e0b',
    iconBg: '#fffbeb',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19h16" />
        <path d="M7 16V8" />
        <path d="M12 16V5" />
        <path d="M17 16v-4" />
      </svg>
    ),
  },
  {
    id: 'strategic-insight',
    title: 'Strategic insight',
    description: 'Surface the takeaways that matter. Spot the key metrics, inflection points, and signals driving the story.',
    iconColor: '#6366f1',
    iconBg: '#eef2ff',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 14l3-3 3 2 4-5" />
      </svg>
    ),
  },
  {
    id: 'podcast-briefings',
    title: 'Podcast for each report',
    description: 'Take the brief with you. Finish with an audio version you can catch up on away from the screen.',
    iconColor: '#f43f5e',
    iconBg: '#fff1f2',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 18a3 3 0 003-3V9a3 3 0 10-6 0v6a3 3 0 003 3z" />
        <path d="M19 10v5a7 7 0 01-14 0v-5" />
        <path d="M12 22v-4" />
      </svg>
    ),
  },
]

interface StackedFeatureCardsProps {
  reducedMotion?: boolean
}

export default function StackedFeatureCards({ reducedMotion = false }: StackedFeatureCardsProps) {
  const renderFeatureCard = useCallback((feature: LandingProductFeature, index: number) => {
    const displayIndex = String(index + 1).padStart(2, '0')

    return (
      <article
        className="landing-feature-card landing-feature-card--stacked"
        data-testid="landing-feature-stack-card"
        style={{
          ['--landing-feature-accent' as string]: feature.iconColor,
          ['--landing-feature-accent-soft' as string]: `${feature.iconColor}22`,
          ['--landing-feature-accent-border' as string]: `${feature.iconColor}66`,
        }}
      >
        <div className="landing-feature-card-border" aria-hidden="true" />
        <div
          className="landing-feature-card-orb"
          aria-hidden="true"
          style={{
            background: `radial-gradient(circle at center, ${feature.iconColor}1f 0%, ${feature.iconColor}00 72%)`,
          }}
        />
        <div className="landing-feature-card-header">
          <div className="landing-feature-icon landing-feature-icon--stacked" style={{ background: feature.iconBg, color: feature.iconColor }}>
            {feature.icon}
          </div>
          <span className="landing-feature-card-step">{displayIndex}</span>
        </div>
        <div className="landing-feature-card-copy">
          <p className="landing-feature-title landing-feature-title--stacked">{feature.title}</p>
          <p className="landing-feature-desc landing-feature-desc--stacked">{feature.description}</p>
        </div>
      </article>
    )
  }, [])

  return (
    <StackedCards
      items={PRODUCT_FEATURES}
      reducedMotion={reducedMotion}
      renderCard={renderFeatureCard}
      className="landing-feature-stack"
      itemClassName="landing-feature-stack-item"
      cardClassName="landing-feature-stack-shell"
      scaleStep={0.034}
      minScale={0.82}
      stickyOffsetPx={88}
      liftAmountPx={36}
      scrub={0.48}
    />
  )
}
